import React, { useCallback, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { Select } from '@factorialco/f0-react/dist/experimental'
import {
  ChevronDown,
  ChevronUp,
  Download,
  Minus,
  Plus,
  Printer,
} from '@factorialco/f0-react/icons/app'
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import LoadingSpinner from './LoadingSpinner'
import ToolbarButton from './ToolbarButton'
import * as styles from './index.vanilla.css'
import { downloadPdf, printPdf } from './pdfUtils'
import { ScaleType, Scales } from './types'
import { calculateVisiblePage } from './viewUtils'

interface ExtendedPDFPageProxy extends PDFPageProxy {
  originalHeight: number
  originalWidth: number
}

export interface Props {
  url: string
  page?: number
  pagesToDisplay?: number[]
  initialScale?: ScaleType
  onPdfLoaded?: (pdf: PDFDocumentProxy) => void
}

export const PdfViewer = (props: Props) => {
  const { url, pagesToDisplay = [], page = 0, initialScale = 'page-width', onPdfLoaded } = props
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null)
  const [pages, setPages] = useState<ExtendedPDFPageProxy[]>([])
  const [scale, setScale] = useState(1)
  const toolbarElement = useRef<HTMLDivElement>(null)
  const containerElement = useRef<HTMLDivElement>(null)
  const [currentPage, setCurrentPage] = useState(0)
  const pageElements = useRef<(HTMLElement | null)[]>([])
  const scrollTimeoutRef = useRef<number | null>(null)
  const totalPages = pagesToDisplay.length > 0 ? pagesToDisplay.length : pdf?.numPages

  // Virtualization settings
  const BUFFER_SIZE = 2
  const LARGE_PDF_THRESHOLD = 5

  const onPageFit = useCallback(
    (pdfPage: ExtendedPDFPageProxy, parentElement: HTMLElement): number => {
      const pageHeight = pdfPage.originalHeight
      const containerHeight =
        parentElement.clientHeight - (toolbarElement.current?.offsetHeight || 0) - 20
      return containerHeight / pageHeight
    },
    []
  )

  const onPageWidth = useCallback(
    (pdfPage: ExtendedPDFPageProxy, parentElement: HTMLElement): number => {
      const pageWidth = pdfPage.originalWidth
      const containerWidth = parentElement.clientWidth - 20
      return containerWidth / pageWidth
    },
    []
  )

  const onPageAdjust = useCallback(
    (
      value: ScaleType,
      callback: (pdfPage: ExtendedPDFPageProxy, parentElement: HTMLElement) => number
    ) => {
      const pageIndex = currentPage - 1

      if (pageIndex < 0 || !pages.length) {
        return
      }

      const pdfPage = pages[pageIndex]

      const parentElement = containerElement.current
      if (parentElement && pdfPage) {
        const expectedScale = callback(pdfPage, parentElement)
        setScale(expectedScale)
      }
      setSelectedScale(value)
    },
    [currentPage, pages]
  )

  const setScaleFromValue = useCallback((value: ScaleType): void => {
    setScale(Number(value))
    setSelectedScale(value)
  }, [])

  const changeScaleToNextStep = useCallback(
    (
      scaleOptions: typeof scales,
      compareScales: (scaleValue: number, currentScale: number) => boolean
    ) => {
      const newScale = scaleOptions.find((scaleOption) => {
        const scaleValue = Number(scaleOption.value)

        return compareScales(scaleValue, scale)
      })

      newScale?.callback(newScale.value)
    },
    [scale]
  )

  const goToPage = useUnsafeCallback(
    (targetPageIndex: number) => {
      if (!pdf) return

      let destinationPageIndex: number = targetPageIndex
      const lastPageIndex = (totalPages || 1) - 1

      if (destinationPageIndex > lastPageIndex) {
        destinationPageIndex = lastPageIndex
      } else if (destinationPageIndex < 0) {
        destinationPageIndex = 0
      }

      const element = pageElements.current[destinationPageIndex]
      const parentContainer = element?.offsetParent
      if (parentContainer) {
        parentContainer.scrollTop =
          element.offsetTop - (toolbarElement.current?.offsetHeight || 0) - 10
      }
    },
    [pdf, totalPages]
  )

  useEffect(() => {
    if (pdf) {
      goToPage(page)
    }
  }, [page])

  const defaultScale = initialScale || 'page-width'
  const [selectedScale, setSelectedScale] = useState<ScaleType>(defaultScale)

  const scales: Scales[] = useMemo(
    () => [
      {
        label: i18n.t('ats.cv_viewer.page_width'),
        value: 'page-width',
        callback: (value: ScaleType) => onPageAdjust(value, onPageWidth),
      },
      {
        label: i18n.t('ats.cv_viewer.page_fit'),
        value: 'page-fit',
        callback: (value: ScaleType) => onPageAdjust(value, onPageFit),
      },
      {
        label: '50%',
        value: '0.5',
        callback: setScaleFromValue,
      },
      {
        label: '75%',
        value: '0.75',
        callback: setScaleFromValue,
      },
      {
        label: '100%',
        value: '1',
        callback: setScaleFromValue,
      },
      {
        label: '125%',
        value: '1.25',
        callback: setScaleFromValue,
      },
      {
        label: '150%',
        value: '1.5',
        callback: setScaleFromValue,
      },
      {
        label: '200%',
        value: '2',
        callback: setScaleFromValue,
      },
      {
        label: '300%',
        value: '3',
        callback: setScaleFromValue,
      },
      {
        label: '400%',
        value: '4',
        callback: setScaleFromValue,
      },
    ],
    [currentPage, pages.length]
  )

  const onChangeScale = useCallback(
    (scaleValue: ScaleType) => {
      const scaleOption = scales.find((s) => s.value === scaleValue)

      scaleOption?.callback(scaleValue)
    },
    [currentPage, pages.length]
  )

  useEffect(() => {
    if (initialScale === 'page-width' || initialScale === 'page-fit') {
      return
    }
    setScale(Number(initialScale))
  }, [initialScale])

  useEffect(() => {
    if (pages.length > 0 && (initialScale === 'page-width' || initialScale === 'page-fit')) {
      onChangeScale(initialScale)
    }
  }, [pages.length, initialScale])

  useEffect(() => {
    if (!containerElement.current) return

    const handleClick = (event: Event) => {
      const target = event.target
      if (target instanceof HTMLAnchorElement) {
        target.target = '_blank'
        target.rel = 'noopener noreferrer'
      }
    }

    const el = containerElement.current
    el.addEventListener('click', handleClick, true)

    return () => {
      el.removeEventListener('click', handleClick, true)

      // Cleanup scroll timeout on unmount
      if (scrollTimeoutRef.current) {
        cancelAnimationFrame(scrollTimeoutRef.current)
      }
    }
  }, [])

  const file = useMemo(() => {
    const pdfjsWithOptions: typeof pdfjs & { defaultDocumentOptions?: Record<string, unknown> } =
      pdfjs
    const defaultOptions = pdfjsWithOptions.defaultDocumentOptions || {}

    return {
      url,
      withCredentials: true,
      cMapPacked: true,
      ...defaultOptions,
    }
  }, [url])

  useEffect(() => {
    setPdf(null)
    pageElements.current = []
    setPages([])
    setCurrentPage(0)

    // Clear any pending scroll calculations
    if (scrollTimeoutRef.current) {
      cancelAnimationFrame(scrollTimeoutRef.current)
      scrollTimeoutRef.current = null
    }
  }, [url])

  const onDocumentLoadSuccess = useCallback(
    (pdfDoc: PDFDocumentProxy) => {
      setPdf(pdfDoc)

      if (onPdfLoaded) onPdfLoaded(pdfDoc)

      // Use requestAnimationFrame instead of setTimeout for better performance
      requestAnimationFrame(() => {
        setCurrentPage(page + 1)
        goToPage(page)
      })
    },
    [page]
  )

  // Uses requestAnimationFrame to throttle scroll calculations, providing smoother performance
  const onContainerScroll = useCallback((event: React.BaseSyntheticEvent) => {
    if (scrollTimeoutRef.current) {
      cancelAnimationFrame(scrollTimeoutRef.current)
    }

    scrollTimeoutRef.current = requestAnimationFrame(() => {
      const visiblePage = calculateVisiblePage(
        event,
        pageElements.current,
        toolbarElement.current?.offsetHeight || 0
      )

      if (!visiblePage) return

      setCurrentPage(visiblePage)
    })
  }, [])

  const increaseScale = useCallback(
    () => changeScaleToNextStep(scales, (scaleValue, currentScale) => scaleValue > currentScale),
    [scales, changeScaleToNextStep]
  )
  const decreaseScale = useCallback(
    () =>
      changeScaleToNextStep(
        [...scales].reverse(),
        (scaleValue, currentScale) => scaleValue < currentScale
      ),
    [scales, changeScaleToNextStep]
  )

  // Cleanup pages when PDF changes
  useUnsafeMemo(() => {
    if (!pdf) {
      setPages([])
      pageElements.current = []
    }
  }, [pdf])

  const handlePageLoadSuccess = useCallback((index: number) => {
    return (pdfPage: ExtendedPDFPageProxy) => {
      setPages((prevPages) => {
        const newPages = [...prevPages]
        newPages[index] = pdfPage
        return newPages
      })
    }
  }, [])

  // Memoize page ref handler
  const handlePageRef = useCallback((index: number) => {
    return (reference: HTMLElement | null) => {
      pageElements.current[index] = reference
    }
  }, [])

  // Page virtualization. Optimize loading of multi page PDFs
  const visiblePageIndices = useMemo(() => {
    if (!totalPages) return []

    if (totalPages <= LARGE_PDF_THRESHOLD) {
      return Array.from({ length: totalPages }, (_, i) => i)
    }

    const currentIndex = Math.max(0, currentPage - 1)
    const startIndex = Math.max(0, currentIndex - BUFFER_SIZE)
    const endIndex = Math.min(totalPages - 1, currentIndex + BUFFER_SIZE)

    return Array.from({ length: endIndex - startIndex + 1 }, (_, i) => startIndex + i)
  }, [totalPages, currentPage, BUFFER_SIZE, LARGE_PDF_THRESHOLD])

  const pagesList = useUnsafeMemo(() => {
    if (!pdf) return null

    const style: React.CSSProperties & { [key: string]: number } = {
      '--scale-factor': scale,
    }

    return visiblePageIndices.map((index) => (
      <div key={index} className={styles.pdfViewerPage} style={style}>
        <Page
          renderForms
          renderTextLayer={false}
          renderAnnotationLayer={false}
          pageNumber={((pagesToDisplay.length > 0 ? pagesToDisplay[index] : index) || 0) + 1}
          scale={scale}
          inputRef={handlePageRef(index)}
          onLoadSuccess={handlePageLoadSuccess(index)}
        />
      </div>
    ))
  }, [pdf, scale, pagesToDisplay, visiblePageIndices, handlePageLoadSuccess, handlePageRef])

  const onPrintPdf = useCallback(() => {
    if (pdf) {
      printPdf(pdf)
    }
  }, [pdf])

  const onDownloadPdf = useCallback(() => {
    if (pdf) {
      const urlParts = url.split('/')
      const filename = urlParts[urlParts.length - 1] || 'document.pdf'
      downloadPdf(pdf, filename)
    }
  }, [pdf, url])

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        cancelAnimationFrame(scrollTimeoutRef.current)
      }

      pageElements.current = []

      if (pdf) {
        pdf.cleanup?.()
      }
    }
  }, [])

  return (
    <div className={styles.pdfViewerContainer}>
      <div
        className={styles.pdfViewerInternalContainer}
        ref={containerElement}
        onScroll={onContainerScroll}
      >
        <Box
          ref={toolbarElement}
          className={styles.pdfViewerToolbar}
          height="s64"
          background="grey000White"
          padding="s16"
          justifyContent="spaceBetween"
          alignItems="center"
          flexDirection="row"
          border={{
            bottom: {
              width: 's1',
              style: 'solid',
              color: 'grey400',
            },
          }}
        >
          <Box gap="s8" flexDirection="row" alignItems="center">
            {pdf?.numPages && (
              <Text.Normal size="200" color="textSecondary" weight="medium">
                {currentPage} / {totalPages}
              </Text.Normal>
            )}
            <ToolbarButton
              ariaLabel={i18n.t('ats.cv_viewer.previous_page')}
              onClick={() => goToPage(currentPage - 2)}
              icon={ChevronUp}
            />
            <ToolbarButton
              ariaLabel={i18n.t('ats.cv_viewer.next_page')}
              onClick={() => goToPage(currentPage)}
              icon={ChevronDown}
            />
          </Box>

          <Box gap="s8" flexDirection="row" alignItems="center">
            <ToolbarButton
              ariaLabel={i18n.t('ats.cv_viewer.zoom_out')}
              onClick={decreaseScale}
              icon={Minus}
            />
            <ToolbarButton
              ariaLabel={i18n.t('ats.cv_viewer.zoom_in')}
              onClick={increaseScale}
              icon={Plus}
            />

            <Select
              hideLabel
              label={i18n.t('ats.cv_viewer.scale_selector')}
              options={scales}
              onChange={(newScale: ScaleType) => onChangeScale(newScale)}
              value={selectedScale}
            />
          </Box>

          <Box gap="s8" flexDirection="row" alignItems="center">
            <ToolbarButton
              ariaLabel={i18n.t('ats.cv_viewer.print')}
              onClick={onPrintPdf}
              icon={Printer}
            />

            <ToolbarButton
              ariaLabel={i18n.t('ats.cv_viewer.download')}
              onClick={onDownloadPdf}
              icon={Download}
            />
          </Box>
        </Box>

        {url && (
          <Document file={file} onLoadSuccess={onDocumentLoadSuccess} loading={<LoadingSpinner />}>
            {pagesList}
          </Document>
        )}
      </div>
    </div>
  )
}
